import React from 'react';
import Footer from '../components/common/Footer';
import Header from '../components/common/Header';
import SEO from '../components/seo/SEO';

const PrivacyPage: React.FC = () => {
  return (
    <>
      <SEO
        title="Privacy Policy - TherapyLink"
        description="TherapyLink Privacy Policy. Learn how we protect your data, ensure FERPA compliance, and maintain the highest standards of privacy for special education therapy services."
        canonical="https://therapy-link.org/privacy"
        robots="index, follow, noarchive"
      />
      <div className="min-h-screen bg-white">
      <Header />
      <main className="section-padding">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-secondary-900 mb-4">
                Privacy Policy
              </h1>
              <p className="text-lg text-secondary-600">
                <strong>Effective Date:</strong> July 07, 2025
              </p>
            </div>

            <div className="prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  1. Introduction
                </h2>
                <p className="text-secondary-700 mb-4">
                  Welcome to TherapyLink, by Red Duck Labs, LLC ("we", "us", or "our"). We are committed to protecting your privacy and handling your personal information with transparency and care. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website{' '}
                  <a 
                    href="https://therapy-link.org"
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    https://therapy-link.org
                  </a>{' '}
                  or use our services.
                </p>
                <p className="text-secondary-700">
                  By accessing or using our website or services, you agree to the terms of this Privacy Policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  2. Information We Collect
                </h2>
                <p className="text-secondary-700 mb-4">
                  We may collect the following types of information:
                </p>
                <ul className="list-disc list-inside text-secondary-700 space-y-2">
                  <li><strong>Email Address:</strong> When you subscribe to updates or contact us.</li>
                  <li><strong>Contact Information:</strong> Such as your name, address, phone number (when signing up for services).</li>
                  <li><strong>Payment Information:</strong> Including credit card details and billing address (when purchasing services).</li>
                  <li><strong>Usage Data:</strong> Information about how you interact with our website (e.g., IP address, browser type).</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  3. How We Use Your Information
                </h2>
                <p className="text-secondary-700 mb-4">
                  We use your personal information for purposes including:
                </p>
                <ul className="list-disc list-inside text-secondary-700 space-y-2">
                  <li>Providing and maintaining our services</li>
                  <li>Processing payments and fulfilling orders</li>
                  <li>Communicating with you about updates or offers</li>
                  <li>Responding to inquiries or support requests</li>
                  <li>Improving our website and services</li>
                  <li>Complying with legal obligations</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  4. Legal Bases for Processing (GDPR)
                </h2>
                <p className="text-secondary-700 mb-4">
                  If you are located in the European Economic Area (EEA), we process your personal data under the following legal bases:
                </p>
                <ul className="list-disc list-inside text-secondary-700 space-y-2">
                  <li>Your consent</li>
                  <li>Performance of a contract</li>
                  <li>Compliance with legal obligations</li>
                  <li>Our legitimate interests</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  5. Sharing Your Information
                </h2>
                <p className="text-secondary-700 mb-4">
                  We do not sell your personal information. We may share your data with:
                </p>
                <ul className="list-disc list-inside text-secondary-700 space-y-2">
                  <li>Service providers who assist in payment processing, email delivery, hosting, etc.</li>
                  <li>Legal authorities if required by law or to protect rights and safety</li>
                  <li>Other parties with your consent</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  6. Cookies & Tracking Technologies
                </h2>
                <p className="text-secondary-700">
                  We may use cookies and similar technologies to enhance user experience and analyze site usage. You can manage cookie preferences through your browser settings.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  7. Your Rights
                </h2>
                
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                    California Residents (CCPA/CPRA)
                  </h3>
                  <p className="text-secondary-700 mb-4">You have the right to:</p>
                  <ul className="list-disc list-inside text-secondary-700 space-y-2 mb-4">
                    <li>Know what personal information we collect, use, disclose, or sell</li>
                    <li>Request deletion of your personal information</li>
                    <li>Opt-out of the sale or sharing of personal information (we do not sell)</li>
                    <li>Non-discrimination for exercising these rights</li>
                  </ul>
                  <p className="text-secondary-700">
                    To exercise these rights, contact us at{' '}
                    <a 
                      href="mailto:privacy@therapy-link.org"
                      className="text-primary-600 hover:text-primary-700 underline"
                    >
                      privacy@therapy-link.org
                    </a>.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                    EU/EEA Residents (GDPR)
                  </h3>
                  <p className="text-secondary-700 mb-4">You have the right to:</p>
                  <ul className="list-disc list-inside text-secondary-700 space-y-2 mb-4">
                    <li>Access, correct, update or request deletion of your personal data</li>
                    <li>Object to processing or request restriction</li>
                    <li>Data portability</li>
                    <li>Withdraw consent at any time</li>
                  </ul>
                  <p className="text-secondary-700">
                    To exercise these rights, contact us at{' '}
                    <a 
                      href="mailto:privacy@therapy-link.org"
                      className="text-primary-600 hover:text-primary-700 underline"
                    >
                      privacy@therapy-link.org
                    </a>.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  8. International Data Transfers
                </h2>
                <p className="text-secondary-700">
                  Your information may be transferred to—and maintained on—servers located outside of your state or country. We take steps to ensure appropriate safeguards are in place.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  9. Data Security
                </h2>
                <p className="text-secondary-700">
                  We implement reasonable measures to protect your information from unauthorized access, disclosure, alteration, or destruction.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  10. Children's Privacy
                </h2>
                <p className="text-secondary-700">
                  Our website is not intended for children under the age of 16. We do not knowingly collect personal data from children.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  11. Changes to This Privacy Policy
                </h2>
                <p className="text-secondary-700">
                  We may update this policy from time to time. Changes will be posted on this page with an updated effective date.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  12. Contact Us
                </h2>
                <p className="text-secondary-700 mb-4">
                  If you have questions about this Privacy Policy or wish to exercise your rights:
                </p>
                <p className="text-secondary-700">
                  Email:{' '}
                  <a 
                    href="mailto:privacy@therapy-link.org"
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    privacy@therapy-link.org
                  </a>
                </p>
              </section>

              <div className="border-t border-secondary-200 pt-6 mt-8">
                <p className="text-sm text-secondary-500 italic">
                  Last updated: July 23, 2025
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
    </>
  );
};

export default PrivacyPage;